import type { GlobalPCSetModel, PricingCampaignModel } from '@/shared/generated'
import type {
	EnrichedGlobalPcSet,
	GlobalPcSetsWorkspaceItemPanel,
} from '../model/types'

import React, {
	createContext,
	use,
	useCallback,
	useMemo,
	useState,
} from 'react'
import { filter, groupBy, map, orderBy, pipe, prop } from 'lodash/fp'
import { useDebounceValue } from 'usehooks-ts'

import {
	useGlobalPcSetsQuery,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { useMutationObservers } from '@/shared/lib/useMutationObservers'

import { filterGlobalPcSets } from '../lib/filterGlobalPcSets'
import { useGroupToggle } from '../lib/useGroupToggle'
import { usePanelsStore } from '../lib/usePanelsStore'

type PanelUpdate = Partial<Omit<GlobalPcSetsWorkspaceItemPanel, 'id'>>

type GlobalPcSetsContextType = {
	isLoading: boolean
	isError: boolean
	panels: GlobalPcSetsWorkspaceItemPanel[]
	updatePanel: (
		id: string,
		data: Partial<Omit<GlobalPcSetsWorkspaceItemPanel, 'id'>>
	) => void
	addPanel: (panel?: PanelUpdate) => void
	replaceLastPanel: (panel: PanelUpdate) => void
	removePanel: (id: string) => void
	searchText: string
	setSearchText: React.Dispatch<React.SetStateAction<string>>
	isGroupExpanded: (id: string) => boolean
	globalPcSets: EnrichedGlobalPcSet[]
	toggleGroup: (id: string) => void
	toggleAllGroups: () => void
	isAllGroupsCollapsed: boolean
	selectedPcIds: string[]
	setSelectedPcIds: React.Dispatch<React.SetStateAction<string[]>>
	selectedGlobalSetIds: string[]
	toggleGlobalSetSelection: (id: string) => void
	setSelectedGlobalSetIds: React.Dispatch<React.SetStateAction<string[]>>
}

const orderPricingCampaigns = orderBy(['order'], ['asc'])

export const GlobalPcSetsContext = createContext<GlobalPcSetsContextType>({
	isLoading: false,
	isError: false,
	panels: [],
	updatePanel: () => {},
	addPanel: () => {},
	removePanel: () => {},
	replaceLastPanel: () => {},
	searchText: '',
	setSearchText: () => {},
	isGroupExpanded: () => false,
	globalPcSets: [],
	toggleGroup: () => {},
	toggleAllGroups: () => {},
	isAllGroupsCollapsed: false,
	selectedPcIds: [],
	setSelectedPcIds: () => {},
	selectedGlobalSetIds: [],
	toggleGlobalSetSelection: () => {},
	setSelectedGlobalSetIds: () => {},
})

export function GlobalPcSetsProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const { panels, updatePanel, addPanel, removePanel } = usePanelsStore()
	const [selectedPcIds, setSelectedPcIds] = useState<string[]>([])
	const [selectedGlobalSetIds, setSelectedGlobalSetIds] = useState<string[]>([])

	const toggleGlobalSetSelection = useCallback((id: string) => {
		setSelectedGlobalSetIds((prev) =>
			prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
		)
	}, [])
	const [search, setSearch] = useState('')
	const [debouncedSearch] = useDebounceValue(search, 500)
	useMutationObservers({
		mutationKey: ['pricing-campaign-delete'],
		onSuccess: (id) => {
			const panel = panels.find((panel) => panel?.pricingCampaignId === id)

			return panel && removePanel(panel.id)
		},
	})
	/**
	 * Queries
	 */
	const {
		data: pricingCampaignsMap,
		isLoading: isPricingCampaignsLoading,
		isError: isPricingCampaignsError,
	} = usePricingCampaignsQuery<Record<string, PricingCampaignModel[]>>(
		{ only_global: true, only_main_global: false },
		{
			select: groupBy<PricingCampaignModel>('global_pc_set_id'),
		}
	)
	const {
		data: globalPcSets,
		isLoading: isGlobalSetsLoading,
		isError: isGlobalSetsError,
	} = useGlobalPcSetsQuery<EnrichedGlobalPcSet[]>({
		enabled: !!pricingCampaignsMap,
		select: pipe(
			orderBy<GlobalPCSetModel>(['is_main', 'name'], ['desc', 'asc']),
			map((globalPcSet: GlobalPCSetModel) => {
				const pricingCampaigns = pricingCampaignsMap![globalPcSet.id] || []

				const filteredCampaigns = selectedPcIds.length
					? filter((pc: PricingCampaignModel) => selectedPcIds.includes(pc.id))(
							pricingCampaigns
						)
					: pricingCampaigns

				return {
					...globalPcSet,
					pricingCampaigns: orderPricingCampaigns(filteredCampaigns),
				}
			}),
			filter((globalPcSet) =>
				selectedPcIds.length > 0
					? globalPcSet.pricingCampaigns.length > 0
					: !!globalPcSet.pricingCampaigns
			)
		),
	})

	/**
	 * Computed values
	 */

	const filteredGlobalPcSets = useMemo(
		() => filterGlobalPcSets(debouncedSearch, globalPcSets),
		[globalPcSets, debouncedSearch]
	)
	const globalPcSetIds = useMemo(
		() => map(prop('id'))(filteredGlobalPcSets),
		[filteredGlobalPcSets]
	)
	const {
		isAllGroupsCollapsed,
		isGroupExpanded,
		toggleGroup,
		toggleAllGroups,
	} = useGroupToggle(globalPcSetIds)

	/**
	 * Handlers
	 */
	const replaceLastPanel = useCallback(
		(panel: PanelUpdate) => {
			const lastPanel = panels[panels?.length - 1]
			if (lastPanel) {
				updatePanel(lastPanel.id, panel)
			} else {
				addPanel(panel)
			}
		},
		[panels, addPanel, updatePanel]
	)

	const value = useMemo(
		() => ({
			isLoading: isPricingCampaignsLoading || isGlobalSetsLoading,
			isError: isPricingCampaignsError || isGlobalSetsError,
			panels,
			updatePanel,
			addPanel,
			removePanel,
			replaceLastPanel,
			searchText: search,
			setSearchText: setSearch,
			globalPcSets: filteredGlobalPcSets || [],
			isGroupExpanded,
			toggleGroup,
			toggleAllGroups,
			isAllGroupsCollapsed,
			selectedPcIds,
			setSelectedPcIds,
			selectedGlobalSetIds,
			setSelectedGlobalSetIds,
			toggleGlobalSetSelection,
		}),
		[
			panels,
			updatePanel,
			addPanel,
			removePanel,
			replaceLastPanel,
			search,
			setSearch,
			filteredGlobalPcSets,
			isGroupExpanded,
			toggleGroup,
			toggleAllGroups,
			isAllGroupsCollapsed,
			selectedPcIds,
			setSelectedPcIds,
			selectedGlobalSetIds,
			setSelectedGlobalSetIds,
			toggleGlobalSetSelection,
		]
	)

	return (
		<GlobalPcSetsContext value={value as GlobalPcSetsContextType}>
			{children}
		</GlobalPcSetsContext>
	)
}

export const usePanelController = (id: string) => {
	const context = use(GlobalPcSetsContext)

	if (!context) {
		throw new Error(
			'useGlobalPcSets must be used within a GlobalPcSetsProvider'
		)
	}

	return useMemo(
		() => ({
			panel: context.panels.find((panel) => panel.id === id),
			addPanel: (panel: PanelUpdate) => context.addPanel(panel),
			removePanel: () => context.removePanel(id),
			updatePanel: (
				data: Partial<Omit<GlobalPcSetsWorkspaceItemPanel, 'id'>>
			) => context.updatePanel(id, data),
		}),
		[context, id]
	)
}
export const useGlobalPcSets = () => {
	const context = use(GlobalPcSetsContext)
	if (!context) {
		throw new Error(
			'useGlobalPcSets must be used within a GlobalPcSetsProvider'
		)
	}

	return context
}
